import interstellarApi from './interstellarApi';

/** Fila de `GET /user/` (unión phone DB + Cosmos opcional + FreeSWITCH opcional) */
export interface InterstellarUserRow {
  id?: number | string;
  cosmos_user_id?: string | null;
  phone?: string | null;
  role_id?: number | null;
  is_active?: boolean | number | null;
  firstName?: string | null;
  lastName?: string | null;
  /** snake_case aliases — some endpoints may return these instead */
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  username?: string | null;
  status?: string | null;
  last_status_change?: string | null;
  /** Whether the user can receive inbound calls via call center queues */
  in_call_center?: boolean;
}

export interface UsersListResponse {
  total_count: number;
  page: number;
  page_size: number;
  users: InterstellarUserRow[];
}

export interface FetchAccountUsersOptions {
  pageSize?: number;
  /** Con API key suele fallar el hidrato Cosmos; `false` devuelve filas de la DB phone. */
  hydrate?: boolean;
}

/**
 * Usuarios de una cuenta: `GET /user/?account_id=&page=&page_size=&hydrate=`
 * Super admin / API key: `account_id` en query (ver `get_account_id` en Interstellar).
 */
export const fetchAccountUsers = async (
  accountId: number,
  options?: FetchAccountUsersOptions
): Promise<UsersListResponse> => {
  const response = await interstellarApi.get<UsersListResponse>('/user/', {
    params: {
      account_id: accountId,
      page: 1,
      page_size: options?.pageSize ?? 200,
      hydrate: options?.hydrate ?? false,
    },
  });
  return response.data;
};

/** Payload para crear un usuario (coincide con AccountUser en interstellar) */
export interface CreateUserPayload {
  cosmos_user_id: number;
  is_admin: boolean;
  /** Extensión SIP opcional de 4 dígitos (0-9999) */
  phone?: number | null;
}

export interface CreateUserResponse {
  message: string;
  id: number;
}

/**
 * Crea uno o más usuarios en una cuenta: `POST /user/?account_id=`
 */
export const createUser = async (
  accountId: number,
  users: CreateUserPayload[]
): Promise<CreateUserResponse> => {
  const response = await interstellarApi.post<CreateUserResponse>('/user/', users, {
    params: { account_id: accountId },
  });
  return response.data;
};

/** Payload para actualizar un usuario */
export interface UpdateUserPayload {
  cosmos_user_id?: number;
  firstName?: string;
  lastName?: string;
  is_admin?: boolean;
  in_call_center?: boolean;
}

export interface UpdateUserResponse {
  message: string;
  id: number;
}

/**
 * Actualiza un usuario existente: `PUT /user/{userId}?account_id=`
 */
export const updateUser = async (
  userId: number | string,
  accountId: number,
  payload: UpdateUserPayload
): Promise<UpdateUserResponse> => {
  const response = await interstellarApi.put<UpdateUserResponse>(`/user/${userId}`, payload, {
    params: { account_id: accountId },
  });
  return response.data;
};
